import { FC, useEffect, useState } from 'react';
import { Column } from 'react-table';
import { FaDownload } from 'react-icons/fa';

import styled from 'styled-components';
import Api from 'core/Api';

import Button from 'components/Button';
import FetchTable from 'components/table/FetchTable';
import TooltipWrapper from 'components/TooltipWrapper';

import Form from 'components/form/Form';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useForm } from 'react-hook-form';
import FormGroup from 'components/form/FormGroup';

import { IField, IForm } from '../types';

export interface IProps {
  entityName: string;
  entityEndpoint: string;
  reportDownLoadURL?: string;
  showDownloadButton?: boolean;
}

const List: FC<Readonly<IProps>> = ({ entityName, entityEndpoint, reportDownLoadURL, showDownloadButton }: IProps) => {
  const [reportBtnLoading, setReportBtnLoading] = useState<Record<string, boolean>>({});
  const [report, setReport] = useState<null | { route: string; key: string }>(null);
  const [isSetFilters, setIsSetFilters] = useState<boolean>(false);
  const [allFilters, setAllFilters] = useState<IForm>({
    date_from: null,
    date_to: null,
    include_demo_clients: false,
  });

  useEffect(() => {
    if (report) {
      getReport(report.route, report.key);
    }
  }, [report]);

  const methods = useForm<IForm>({
    defaultValues: {},
  });

  const { reset } = methods;

  const getReport = (route: string, key: string) => {
    const formattedDateFrom = formatDate(allFilters.date_from);
    const formattedDateTo = formatDate(allFilters.date_to);

    let url = `${route}?include_demo_clients=${allFilters.include_demo_clients}`;

    if (allFilters.date_from) {
      url += `&date_from=${formattedDateFrom}`;
    }

    if (!formattedDateFrom && formattedDateTo) {
      url += `&date_to=${formattedDateTo}`;
    }

    Api.get(url)
      .then((res: any) => {
        window.open(res.data.url, '_blank');
        setReportBtnLoading(prevState => ({
          ...prevState,
          [key]: false,
        }));
      })
      .catch(err => console.error(err));
  };

  const formatDate = (date: Date | null): string | null => {
    if (!date) {
      return null;
    }

    const formattedDate = `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(
      date.getDate()
    ).padStart(2, '0')}`;

    return formattedDate;
  };

  const onSubmit = (data: IForm): void => {
    setAllFilters(data);
    setIsSetFilters(true);
  };

  const columns: Array<Column<IField>> = [
    {
      Header: 'Име',
      accessor: 'name',
      width: '20%',
    },
    {
      Header: 'Тенант',
      accessor: 'tenantname',
      width: '20%',
    },
    {
      Header: 'Активни юзъри',
      accessor: 'active_count',
      width: '20%',
      disableSortBy: true,
    },
    {
      Header: 'Неактивни юзъри',
      accessor: 'inactive_count',
      width: '20%',
      disableSortBy: true,
    },
    {
      Header: 'Свали справка',
      accessor: 'actions',
      width: '10%',
      disableSortBy: true,
      Cell: ({ row: { original } }: any) => {
        const { tenantname } = original;
        return (
          <span className="mr-2">
            <TooltipWrapper
              id="tooltip-resend"
              overlay="Свали справка за потребител"
            >
              <Button
                btnType="secondary"
                size="sm"
                outlined
                disabled={!isSetFilters}
                loading={reportBtnLoading[tenantname]}
                onClick={() => {
                  setReportBtnLoading(prevState => ({
                    ...prevState,
                    [tenantname]: true,
                  }));
                  getReport(`${reportDownLoadURL}/${tenantname}`, tenantname);
                }}
              >
                <FaDownload size={14} />
              </Button>
            </TooltipWrapper>
          </span>
        );
      },
    },
  ];

  return (
    <Wrapper>
      {showDownloadButton && (
        <div className="mb-3 text-center">
          <Button
            btnType="secondary"
            disabled={reportBtnLoading.all}
            loading={reportBtnLoading.all}
            onClick={() => {
              setReportBtnLoading(prevState => ({
                ...prevState,
                all: true,
              }));
              getReport(`${reportDownLoadURL}`, 'all');
            }}
          >
            <FaDownload
              size={14}
              className="mr-2"
            />
            Свали справка използване на системата за всички клиенти
          </Button>
        </div>
      )}
      <div>
        <Row className="mt-4">
          <Col md={{ offset: 2, span: 8 }}>
            <Form
              methods={methods}
              onSubmit={onSubmit}
              onCancel={() => {
                reset();
                setIsSetFilters(false);
              }}
              btnLabel="Задай филтър"
              isHorizontal
              areBtnsCentered
            >
              <FormGroup
                controlId="formGroupDateOfBirth"
                label="Начална дата"
                type="datepicker"
                name="date_from"
                dateFormat="dd/MM/yyyy"
                maxDate={new Date()}
                isHorizontal
                showMonthDropdown
                showYearDropdown
                isDisabled={isSetFilters}
              />
              <FormGroup
                controlId="formGroupToDate"
                label="Крайна дата"
                type="datepicker"
                name="date_to"
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                isHorizontal
                isDisabled={isSetFilters}
              />
              <FormGroup
                controlId="formGroupMassDoc"
                label="Включи демо компании"
                checkType="switch"
                name="include_demo_clients"
                showMonthDropdown
                showYearDropdown
                isHorizontal
                isDisabled={isSetFilters}
              />
            </Form>
          </Col>
        </Row>

        <p className="mt-3 text-center text-danger">
          Филтрите важат за бутоните, които свалят справка. Ако се избере период се виждат активирани потребители само в
          този срок. Ако има само крайна дата се вижда общ брой.
        </p>
        <div className="pt-2 pl-3 text-center">
          <Button
            btnType="secondary"
            disabled={!isSetFilters}
            loading={reportBtnLoading.all}
            onClick={() => {
              setReportBtnLoading(prevState => ({
                ...prevState,
                all: true,
              }));
              setReport({ route: `${reportDownLoadURL}`, key: 'all' });
            }}
          >
            <FaDownload
              size={14}
              className="mr-2"
            />
            Свали справка използване на системата за всички клиенти
          </Button>
        </div>
      </div>
      <FetchTable<IField>
        entityName={entityName}
        entityEndpoint={entityEndpoint}
        columns={columns}
        pagination
      />
    </Wrapper>
  );
};

export const Wrapper = styled.div`
  display: flex;
  flex-grow: 1;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;

  @media (max-width: 992px) {
    flex-direction: column;
    align-items: center;
  }
`;

export default List;
